import { Link } from '@inertiajs/react'
import { useState } from 'react'

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false)

  return (
    <section id="pricing" className="py-20 sm:py-24" style={{ backgroundColor: '#f8f7f4' }}>
      <div className="max-w-[1140px] mx-auto px-8">
        <p className="text-[11px] font-mono uppercase tracking-[2px] text-[#1a7a4c] font-medium mb-3">
          Pricing
        </p>
        <h2 className="font-display text-3xl sm:text-[40px] leading-[1.15] text-[#0f1a2e] tracking-tight mb-4">
          Simple pricing. No surprises.
        </h2>
        <p className="text-[17px] leading-relaxed text-[#5a6578] max-w-[600px] mb-9">
          Start with the free calculators. Pay when you're tracking real loans.
        </p>

        {/* Billing toggle */}
        <div className="flex items-center gap-3 mb-9">
          <span
            className={`text-sm ${!isAnnual ? 'font-semibold text-[#0f1a2e]' : 'font-medium text-[#8c95a6]'}`}
          >
            Monthly
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="relative w-12 h-[26px] rounded-full bg-[#1a7a4c] cursor-pointer transition-colors"
          >
            <span
              className={`absolute top-[3px] w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-200 ${
                isAnnual ? 'left-[25px]' : 'left-[3px]'
              }`}
            />
          </button>
          <span
            className={`text-sm ${isAnnual ? 'font-semibold text-[#0f1a2e]' : 'font-medium text-[#8c95a6]'}`}
          >
            Annual
          </span>
          <span className="font-mono text-[11px] bg-[#e8f5ee] text-[#1a7a4c] px-2.5 py-0.5 rounded-full font-medium">
            2 months free
          </span>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-[1000px]">
          {/* Pro — Best Value (featured, first) */}
          <div className="bg-white border-[1.5px] border-[#1a7a4c] rounded-[14px] p-7 sm:p-9 flex flex-col relative shadow-[0_0_0_1px_#1a7a4c,0_8px_32px_rgba(26,122,76,0.12)] order-first lg:order-none hover:-translate-y-1 hover:shadow-[0_0_0_1px_#1a7a4c,0_8px_32px_rgba(26,122,76,0.18)] transition-all">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-4 py-1 bg-[#1a7a4c] text-white font-mono text-[10px] uppercase tracking-[1px] rounded-full font-medium">
                Best Value
              </span>
            </div>
            <h3 className="text-xl font-bold text-[#0f1a2e]">Pro</h3>
            <p className="text-sm text-[#5a6578] mt-1">For active lenders running a real portfolio</p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-display text-5xl text-[#0f1a2e]">${isAnnual ? '41' : '49'}</span>
              <span className="text-base text-[#5a6578]">/mo</span>
            </div>
            {isAnnual && (
              <p className="text-sm text-[#5a6578] mt-1">$490/year — save $98</p>
            )}
            <p className="text-sm text-[#5a6578] mt-1">Up to 25 active loans</p>
            <ul className="mt-6 space-y-2 flex-1">
              {[
                'Everything in Solo',
                'CSV exports (payments & expenses)',
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                  <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                  {f}
                </li>
              ))}
              <li className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                <span>
                  AI deal memos{' '}
                  <span className="inline-block align-middle font-mono text-[9px] tracking-wide px-1.5 py-px rounded bg-[#ede9fe] text-[#6d28d9]">
                    AI
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                <span>
                  AI risk narratives{' '}
                  <span className="inline-block align-middle font-mono text-[9px] tracking-wide px-1.5 py-px rounded bg-[#ede9fe] text-[#6d28d9]">
                    AI
                  </span>
                </span>
              </li>
              {[
                'Branded PDF statements',
                'Automated email reminders',
                'Accountant-ready tax exports (.pdf, .csv, .qbo)',
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                  <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href="/users/sign_up"
              className="mt-7 block text-center py-3.5 rounded-lg text-[15px] font-semibold bg-[#0f1a2e] text-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              Start free trial
            </Link>
          </div>

          {/* Solo */}
          <div className="bg-white border-[1.5px] border-[#d4d2c9] rounded-[14px] p-7 sm:p-9 flex flex-col shadow-[0_2px_12px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.1),0_2px_6px_rgba(0,0,0,0.05)] transition-all">
            <h3 className="text-xl font-bold text-[#0f1a2e]">Solo</h3>
            <p className="text-sm text-[#5a6578] mt-1">For lenders just getting started</p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-display text-5xl text-[#0f1a2e]">${isAnnual ? '24' : '29'}</span>
              <span className="text-base text-[#5a6578]">/mo</span>
            </div>
            {isAnnual && (
              <p className="text-sm text-[#5a6578] mt-1">$290/year — save $58</p>
            )}
            <p className="text-sm text-[#5a6578] mt-1">Up to 5 active loans</p>
            <ul className="mt-6 space-y-2 flex-1">
              {[
                'Deal calculators + ROI tools',
                'Full amortization engine (standard, interest-only, balloon)',
                'Payment tracking + guardrails',
                'Dashboard & basic reports',
                'Spreadsheet import (CSV & Excel)',
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                  <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href="/users/sign_up"
              className="mt-7 block text-center py-3.5 rounded-lg text-[15px] font-semibold bg-transparent text-[#0f1a2e] border-[1.5px] border-[#c5c3ba] hover:border-[#5a6578] hover:bg-[#f8f7f4] transition-all"
            >
              Start free trial
            </Link>
          </div>

          {/* Fund */}
          <div className="bg-white border-[1.5px] border-[#d4d2c9] rounded-[14px] p-7 sm:p-9 flex flex-col shadow-[0_2px_12px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.1),0_2px_6px_rgba(0,0,0,0.05)] transition-all">
            <h3 className="text-xl font-bold text-[#0f1a2e]">Fund</h3>
            <p className="text-sm text-[#5a6578] mt-1">For lenders managing outside capital</p>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="font-display text-5xl text-[#0f1a2e]">${isAnnual ? '82' : '99'}</span>
              <span className="text-base text-[#5a6578]">/mo</span>
            </div>
            {isAnnual && (
              <p className="text-sm text-[#5a6578] mt-1">$990/year — save $198</p>
            )}
            <p className="text-sm text-[#5a6578] mt-1">Unlimited active loans</p>
            <ul className="mt-6 space-y-2 flex-1">
              <li className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                Everything in Pro
              </li>
              <li className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                <span>
                  <Link href="/deal-room" className="text-[#1a7a4c] underline underline-offset-2 hover:text-[#15673f]">
                    Deal Room
                  </Link>
                  {' '}— post offerings, collect soft commitments, lock deals
                </span>
              </li>
              {[
                'Multi-investor per loan with fractional ownership',
                'Pro-rata distributions on every payment',
                'Per-investor annual statements',
                'K-1 data exports for your accountant',
                'Team members with role-based access',
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-[#2d3a4f] leading-relaxed">
                  <span className="text-[#1a7a4c] font-bold text-sm mt-0.5 min-w-4">✓</span>
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-[13px] text-[#8c95a6] leading-relaxed">
              Questions about Fund tier?{' '}
              <a
                href="mailto:support@lendsolo.com?subject=Fund%20tier%20question"
                className="text-[#1a7a4c] underline underline-offset-2 hover:text-[#15673f]"
              >
                Email Pete
              </a>
            </p>
            <Link
              href="/users/sign_up"
              className="mt-3 block text-center py-3.5 rounded-lg text-[15px] font-semibold bg-transparent text-[#0f1a2e] border-[1.5px] border-[#c5c3ba] hover:border-[#5a6578] hover:bg-[#f8f7f4] transition-all"
            >
              Start free trial
            </Link>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-6 text-[13px] text-[#8c95a6] leading-relaxed max-w-[800px]">
          The deal calculator, ROI tool, and loan comparison tool are free forever — no account required. Start there. You'll know when you're ready for the rest.<br />
          All paid plans include a 14-day free trial. No credit card required to start.
        </p>
      </div>
    </section>
  )
}
